import type { CreatePlayerRequest, PlayerSummary, TemplateField, Tournament } from '@brush/api-client';
import { listPhrase, normaliseKey } from './columns.ts';
import { isBlankRow } from './csv.ts';
import {
  canCreatePlayers,
  emptyResultRow,
  missingRequiredResultColumns,
  resultColumnFor,
  type ResultColumn,
} from './resultColumns.ts';

/**
 * Reading a parsed CSV against a room's template into the results it would record.
 *
 * The same shape as `plan.ts`: nothing here talks to the server, the plan is both what the review
 * shows and what the run sends, and it is built the same way either time — so what an operator
 * approves is exactly what happens.
 *
 * Two things make it simpler than the tournament plan. A results file describes **one**
 * tournament, chosen in the UI rather than named in the file, so there is no grouping. And there
 * is no diff: results are replaced wholesale by the PATCH, so a re-import is not a question about
 * what moved — it is a statement that this file is now the record.
 *
 * The one thing it has that the tournament plan does not is players. A card the room does not
 * hold becomes a new player, created before the results that point at it.
 *
 * And the one thing that is not a player: an **unclaimed place** (ADR 0013), a finish the file
 * records while nobody has claimed it. It becomes a result with no player — never a created one —
 * and it is recognised in three shapes, each only on a row with a place (see `UnclaimedReason`).
 */

/**
 * Why a row is an unclaimed place, in the words the review shows beside it. The reason is the
 * operator's check on V1 in particular: a mistyped card on a row with no name reads as "card not at
 * this room", which is how a typo is noticed rather than silently imported as nobody.
 *
 * - V1 `card not at this room and no name` — the source generated a card for a finisher it could
 *   not name. The card is kept as provenance and never used to find or create anyone.
 * - V2 `no card` — the card cell is blank.
 * - V3 `card marks unclaimed` — the card cell holds the marker the room configured on this
 *   template. Matched trimmed and without regard to case, before any player lookup.
 */
export type UnclaimedReason =
  | 'card not at this room and no name'
  | 'no card'
  | 'card marks unclaimed';

export type IssueLevel = 'error' | 'warning';

export interface Issue {
  level: IssueLevel;
  /** The line in the file, 1-based, counting the heading row — what a spreadsheet shows. */
  line?: number;
  message: string;
}

/** One field key from the room's content, resolved against the importer's vocabulary. */
export interface PlannedResultColumn {
  fieldKey: string;
  label: string;
  /** Null where the key isn't one the importer understands, so the column is ignored. */
  column: ResultColumn | null;
}

/** What importing one line would record, and against whom. */
export interface PlannedResult {
  line: number;
  /** Null for somebody who entered and did not reach a paid place. */
  place: number | null;
  cardId: string;
  /** The room's existing player, or null when this row will create one or is an unclaimed place. */
  player: PlayerSummary | null;
  /** Why this row is an unclaimed place, or null when it names a player. */
  unclaimed: UnclaimedReason | null;
  /** V1's card, kept as provenance. Null for every other row. */
  sourceCardId: string | null;
  /** Shown for both cases — the stored name for a match, the file's for a new player. */
  displayName: string;
  city: string | null;
  state: string | null;
  prize: number;
  prizeNotes: string | null;
  isInDeal: boolean;
  buyins: number | null;
}

export interface ResultsPlan {
  columns: PlannedResultColumn[];
  /** Whether the first line was read as headings rather than data. */
  hasHeader: boolean;
  results: PlannedResult[];
  /** Everything wrong, file-level first and then in line order. */
  issues: Issue[];
  errorCount: number;
  warningCount: number;
  dataRows: number;
  blankRows: number;
  /** Players this file names that the room has never carded. Created before the results. */
  newPlayers: CreatePlayerRequest[];
  /** How many results the tournament already has, all of which this import replaces. */
  replacing: number;
}

export interface ResultsPlanInput {
  roomId: string;
  /** The tournament these results belong to. Chosen in the UI — a results file does not name it. */
  tournament: Tournament;
  /** The template's fields, for naming a column the way the room sees it. */
  fields: readonly TemplateField[];
  /** The room's chosen columns, in its own order. */
  fieldKeys: readonly string[];
  rows: string[][];
  /**
   * The room's players holding the file's cards (`fileCardIds`, looked up on the server), for matching a
   * card to somebody it already holds. Any of the room's players will do; these are the ones that matter.
   */
  players: readonly PlayerSummary[];
  /** The template's marker for an unclaimed place (V3), or null where the room configured none. */
  unclaimedMarker?: string | null;
}

/**
 * As many finishers as one run will take on. Generous — a large field pays hundreds of places —
 * but still a bound on what a mis-picked file can do before anyone notices.
 */
export const MAX_RESULTS = 2000;

const error = (message: string, line?: number): Issue => ({ level: 'error', message, line });
const warning = (message: string, line?: number): Issue => ({ level: 'warning', message, line });

/** Case-insensitive, matching the collation the server compares card IDs under. */
const cardKey = (cardId: string) => cardId.trim().toLowerCase();

const empty = (columns: PlannedResultColumn[], issues: Issue[], replacing: number): ResultsPlan => ({
  columns,
  hasHeader: false,
  results: [],
  issues,
  errorCount: issues.filter((i) => i.level === 'error').length,
  warningCount: issues.filter((i) => i.level === 'warning').length,
  dataRows: 0,
  blankRows: 0,
  newPlayers: [],
  replacing,
});

/**
 * The cards a results file names: what the import asks the room about (`POST …/players/by-cards`)
 * before it builds the plan, in place of reading the room's whole roster (performance rank 2).
 */
export function fileCardIds(input: Pick<ResultsPlanInput, 'fields' | 'fieldKeys' | 'rows'>): string[] {
  const at = input.fieldKeys.findIndex((key) => resultColumnFor(key)?.key === 'card_id');
  if (at < 0 || input.rows.length === 0) return [];

  const seen = new Map<string, string>();
  const start = hasHeadingRow(plannedColumns(input), input.rows) ? 1 : 0;
  for (const row of input.rows.slice(start)) {
    if (isBlankRow(row)) continue;
    const card = (row[at] ?? '').trim();
    // Once each, as the plan matches them: case-insensitively.
    if (card !== '' && !seen.has(cardKey(card))) seen.set(cardKey(card), card);
  }
  return [...seen.values()];
}

function plannedColumns(input: Pick<ResultsPlanInput, 'fields' | 'fieldKeys'>): PlannedResultColumn[] {
  const labels = new Map(input.fields.map((f) => [f.key, f.label]));
  return input.fieldKeys.map((fieldKey) => ({
    fieldKey,
    label: labels.get(fieldKey) ?? fieldKey,
    column: resultColumnFor(fieldKey),
  }));
}

/**
 * Whether the first row is a heading row: at least half its filled cells name one of the template's
 * columns. One definition, read by the plan and by `fileCardIds`, so the two cannot disagree about
 * whether row 1 is data.
 */
function hasHeadingRow(columns: readonly PlannedResultColumn[], rows: readonly string[][]): boolean {
  const headingSpellings = new Set(
    columns.flatMap((c) => [normaliseKey(c.fieldKey), normaliseKey(c.label)]),
  );
  const named = (rows[0] ?? []).filter((cell) => cell.trim() !== '');
  const namesAColumn = (cell: string) =>
    headingSpellings.has(normaliseKey(cell)) || resultColumnFor(cell) !== null;
  return named.length > 0 && named.filter(namesAColumn).length * 2 >= named.length;
}

export function buildResultsPlan(input: ResultsPlanInput): ResultsPlan {
  const columns = plannedColumns(input);

  // The stored row count, which is what `entries` is — see the rundown for why it is the same
  // number as `results.length` without needing the array.
  const replacing = input.tournament.entries;
  const fileIssues: Issue[] = [];

  // A column the importer has no use for is a warning rather than an error: the room may well
  // carry it for its own reasons, and dropping it silently would look like data loss.
  for (const planned of columns) {
    if (!planned.column) {
      fileIssues.push(
        warning(
          `Column “${planned.label}” isn't one the importer recognises, so its values are ignored.`,
        ),
      );
    }
  }

  const missing = missingRequiredResultColumns(input.fieldKeys);
  if (missing.length > 0) {
    fileIssues.push(
      error(
        `This template doesn't include ${listPhrase(missing.map((m) => m.label))}. ` +
          `A result can't be recorded without ${missing.length === 1 ? 'it' : 'them'}.`,
      ),
    );
    return empty(columns, fileIssues, replacing);
  }

  if (input.rows.length === 0) {
    fileIssues.push(error('This file has no rows.'));
    return empty(columns, fileIssues, replacing);
  }

  // ---- heading row ----
  // Identical reasoning to the tournament importer: the template decides which column is which,
  // and a heading row that disagrees is an error rather than a positional misread — a card ID
  // landing in the prize column would import quietly and wrongly.

  const firstRow = input.rows[0]!;
  const hasHeader = hasHeadingRow(columns, input.rows);

  if (hasHeader) {
    if (firstRow.length !== columns.length) {
      fileIssues.push(
        error(
          `The heading row has ${firstRow.length} columns but this template declares ` +
            `${columns.length}. Update the template's columns, or the file, so the two agree.`,
        ),
      );
    } else {
      for (let i = 0; i < columns.length; i += 1) {
        const heading = firstRow[i]!;
        const planned = columns[i]!;
        const matches =
          normaliseKey(heading) === normaliseKey(planned.fieldKey) ||
          normaliseKey(heading) === normaliseKey(planned.label);
        if (!matches) {
          fileIssues.push(
            error(
              `Column ${i + 1} is headed “${heading.trim()}” but this template expects ` +
                `“${planned.label}”.`,
            ),
          );
        }
      }
    }
  }

  if (fileIssues.some((i) => i.level === 'error')) {
    return { ...empty(columns, fileIssues, replacing), hasHeader };
  }

  // ---- read every data row ----

  const byCard = new Map(input.players.map((p) => [cardKey(p.cardId), p]));
  const namesAvailable = canCreatePlayers(input.fieldKeys);

  const marker = input.unclaimedMarker?.trim() ? cardKey(input.unclaimedMarker) : null;

  // V1's refusal on a re-import: a card the source generated for a place still unclaimed must not
  // become somebody's card — which is what naming it on a row with a name, or finding a player who
  // happens to hold those digits, would do. Keyed to the place it was generated for, for the message.
  const generated = new Map(
    input.tournament.results
      .filter((r) => r.unclaimed && r.sourceCardId)
      .map((r) => [cardKey(r.sourceCardId!), r.position]),
  );

  const rowIssues: Issue[] = [];
  const planned: PlannedResult[] = [];
  const seenPlaces = new Map<number, number>();
  const seenCards = new Map<string, number>();
  const newPlayers = new Map<string, CreatePlayerRequest>();

  let dataRows = 0;
  let blankRows = 0;
  let unknownWithoutNames = 0;

  for (let index = hasHeader ? 1 : 0; index < input.rows.length; index += 1) {
    const row = input.rows[index]!;
    const line = index + 1;

    if (isBlankRow(row)) {
      blankRows += 1;
      continue;
    }
    dataRows += 1;

    if (planned.length >= MAX_RESULTS) {
      rowIssues.push(
        error(`This file has more than ${MAX_RESULTS} places. Split it and import in parts.`, line),
      );
      break;
    }

    const draft = emptyResultRow();
    const failed = new Set<string>();

    for (let i = 0; i < columns.length; i += 1) {
      const planned_ = columns[i];
      if (!planned_?.column) continue;
      const problem = planned_.column.read(row[i] ?? '', draft);
      if (problem !== null) {
        rowIssues.push(error(`${planned_.label}: ${problem}`, line));
        failed.add(planned_.column.key);
      }
    }

    // V2: a placed row with no card is an unclaimed place. On a row with no place a card is still
    // required — an unclaimed place is a place, and an entrant with no card and no place is nobody
    // anyone could ever claim.
    const noCard = draft.cardId === null && !failed.has('card_id') && draft.place !== null;

    // Required cells, announced only where the cell did not already fail in its own words —
    // "that isn't a place" and "a place is required" are one problem, and printing both reads
    // as two.
    if (draft.cardId === null && !failed.has('card_id') && !noCard) {
      rowIssues.push(error('Card ID is required.', line));
    }
    if (draft.prize === null && !failed.has('prize')) {
      rowIssues.push(error('Prize is required — use 0 for a place that paid nothing.', line));
    }

    if (draft.prize === null || (draft.cardId === null && !noCard)) continue;

    const place = draft.place;
    const card = draft.cardId ?? '';

    // Paid but nowhere: the two cells contradict each other, and picking one would either invent
    // a finishing position or throw away money somebody was paid.
    if (place === null && draft.prize > 0) {
      rowIssues.push(
        error(`This row has a prize of $${draft.prize} but no place. Which is it?`, line),
      );
      continue;
    }

    if (place !== null) {
      const placeSeen = seenPlaces.get(place);
      if (placeSeen !== undefined) {
        rowIssues.push(error(`Place ${place} is already used on line ${placeSeen}.`, line));
        continue;
      }
      seenPlaces.set(place, line);
    }

    // V3: the room's marker, matched before any player lookup. It says nothing about who finished
    // here, so it may stand in several places — unlike a card, which is one person.
    const marked = !noCard && marker !== null && cardKey(card) === marker;

    if (marked && place === null) {
      rowIssues.push(
        error(
          `This row's card is the marker for an unclaimed place, but it has no place. An unclaimed ` +
            `place is a place; an entrant without one needs their own card.`,
          line,
        ),
      );
      continue;
    }

    if (noCard || marked) {
      const reason: UnclaimedReason = noCard ? 'no card' : 'card marks unclaimed';
      const named = `${draft.firstName ?? ''} ${draft.lastName ?? ''}`.trim();
      // Said rather than refused: the row is still an unclaimed place. Said out loud so a file whose
      // card column failed to map cannot turn named players into unclaimed places with nothing on
      // the screen saying so.
      if (named !== '') {
        rowIssues.push(
          warning(
            `The name "${named}" is ignored: this row imports as an unclaimed place (${reason}), ` +
              `and an unclaimed place names nobody.`,
            line,
          ),
        );
      }
      planned.push(unclaimedResult(line, place!, reason, null, draft));
      continue;
    }

    const cardSeen = seenCards.get(cardKey(card));
    if (cardSeen !== undefined) {
      rowIssues.push(error(`Card ${card} already finished on line ${cardSeen}.`, line));
      continue;
    }
    seenCards.set(cardKey(card), line);

    const existing = byCard.get(cardKey(card)) ?? null;
    const noName = draft.firstName === null && draft.lastName === null;

    const generatedFor = generated.get(cardKey(card));
    if (generatedFor !== undefined && (existing !== null || !noName)) {
      rowIssues.push(
        error(
          `Card ${card} is the one the results source generated for place ${generatedFor}, which ` +
            `nobody has claimed. It is not the player's own card — use theirs to claim the place.`,
          line,
        ),
      );
      continue;
    }

    if (existing === null) {
      if (!namesAvailable) {
        // Counted and reported once at file level: fifty rows repeating that the template has no
        // name columns is fifty copies of one problem with the template.
        unknownWithoutNames += 1;
        continue;
      }
      // V1: a placed row whose card the room does not hold and which names nobody. The card is
      // kept as provenance and never becomes anybody's.
      if (noName && place !== null) {
        planned.push(unclaimedResult(line, place, 'card not at this room and no name', card, draft));
        continue;
      }
      if (draft.firstName === null || draft.lastName === null) {
        rowIssues.push(
          error(`Card ${card} isn't a player at this room, and this row has no name to create one.`, line),
        );
        continue;
      }

      newPlayers.set(cardKey(card), {
        cardId: card,
        roomId: input.roomId,
        firstName: draft.firstName,
        lastName: draft.lastName,
        city: draft.city,
        state: draft.state,
        avatar: initialsOf(draft.firstName, draft.lastName),
        // Contact reaches new cards only, and that is a limitation rather than a design: this
        // branch is the one place a player is created, and a matched card is reused untouched.
        //
        // Update-on-match is the obvious follow-up and is deliberately not here, because it
        // carries an unanswered question — may an import *overwrite* a contact already on file?
        // A silent overwrite would let a stale spreadsheet redirect somebody's verification code,
        // which is the one thing this data must not do. The likely answer is fill blanks, never
        // overwrite, and report the conflicts; it wants deciding rather than assuming.
        email: draft.email,
        phone: draft.phone,
      });
    }

    planned.push({
      line,
      place,
      cardId: card,
      player: existing,
      unclaimed: null,
      sourceCardId: null,
      displayName: existing
        ? existing.name
        : `${draft.firstName ?? ''} ${draft.lastName ?? ''}`.trim(),
      city: existing ? (existing.city ?? null) : draft.city,
      state: existing ? (existing.state ?? null) : draft.state,
      prize: draft.prize,
      prizeNotes: draft.prizeNotes,
      // Absent means no. The column is optional and the field is not nullable — a blank cell is
      // the ordinary case, not an unknown one.
      isInDeal: draft.isInDeal ?? false,
      buyins: draft.buyins,
    });
  }

  if (unknownWithoutNames > 0) {
    fileIssues.push(
      error(
        `${unknownWithoutNames} ${unknownWithoutNames === 1 ? 'row names a card' : 'rows name cards'} ` +
          `your room doesn't hold, and this template has no first and last name columns to create ` +
          `${unknownWithoutNames === 1 ? 'that player' : 'those players'} from.`,
      ),
    );
  }

  // Paid places first in order, then the rest of the field in the order the file listed them.
  planned.sort((a, b) => {
    if (a.place === null && b.place === null) return a.line - b.line;
    if (a.place === null) return 1;
    if (b.place === null) return -1;
    return a.place - b.place;
  });

  // The places that *are* filled in must run 1..K. The manual editor derives a place from the
  // row's position and so can never produce a gap; accepting one here would mean an imported
  // sheet changed the next time somebody opened and saved it in that editor.
  //
  // Counted over the placed rows only — the unplaced field is not part of the sequence.
  const placed = planned.filter((r) => r.place !== null);
  if (placed.length > 0) {
    const highest = placed[placed.length - 1]!.place!;
    if (highest !== placed.length) {
      fileIssues.push(
        error(
          `Places have to run 1 to ${placed.length} with no gaps. This file fills in ` +
            `${placed.length} ${placed.length === 1 ? 'place' : 'places'} but reaches ${highest}.`,
        ),
      );
    }
  }

  const issues = [...fileIssues, ...rowIssues.sort((a, b) => (a.line ?? 0) - (b.line ?? 0))];
  const errorCount = issues.filter((i) => i.level === 'error').length;

  return {
    columns,
    hasHeader,
    // One error anywhere blocks the whole run, so a plan that would not be sent shows nothing to
    // approve — the same rule the tournament importer follows, and for the same reason: the run
    // is not atomic, so a partial import is worse than none.
    results: errorCount > 0 ? [] : planned,
    issues,
    errorCount,
    warningCount: issues.filter((i) => i.level === 'warning').length,
    dataRows,
    blankRows,
    newPlayers: errorCount > 0 ? [] : [...newPlayers.values()],
    replacing,
  };
}

/** A row that imports as an unclaimed place: no player, its own figures, and why. */
function unclaimedResult(
  line: number,
  place: number,
  reason: UnclaimedReason,
  sourceCardId: string | null,
  draft: { prize: number | null; prizeNotes: string | null; isInDeal: boolean | null; buyins: number | null },
): PlannedResult {
  return {
    line,
    place,
    cardId: sourceCardId ?? '',
    player: null,
    unclaimed: reason,
    sourceCardId,
    displayName: `Unclaimed — place ${place}`,
    city: null,
    state: null,
    prize: draft.prize ?? 0,
    prizeNotes: draft.prizeNotes,
    isInDeal: draft.isInDeal ?? false,
    buyins: draft.buyins,
  };
}

/** Same rule the manual editor and the add-player dialog use. */
function initialsOf(first: string, last: string): string {
  const letters = [first, last].map((part) => part.trim()[0]?.toUpperCase() ?? '').join('');
  return letters === '' ? '??' : letters;
}
